import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import './DataPage.css';
import Pagination from '../components/Pagination';

import { API_URL } from '../config';
import { formatDate } from '../utils/formatDate';

function Atendimentos() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const pacienteIdFromUrl = searchParams.get('pacienteId');

  // Calcular data padrão (últimos 7 dias) - mas não usar se vier com pacienteId
  const hoje = new Date();
  const seteDiasAtras = new Date();
  seteDiasAtras.setDate(hoje.getDate() - 7);

  const [atendimentos, setAtendimentos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState('');
  const [dataInicio, setDataInicio] = useState(pacienteIdFromUrl ? '' : seteDiasAtras.toISOString().split('T')[0]);
  const [dataFim, setDataFim] = useState(pacienteIdFromUrl ? '' : hoje.toISOString().split('T')[0]);
  const [usuario, setUsuario] = useState('');
  const [situacao, setSituacao] = useState('');
  const [pacienteId, setPacienteId] = useState(pacienteIdFromUrl || '');
  const [convenio, setConvenio] = useState('');
  const [local, setLocal] = useState('');
  const [sortBy, setSortBy] = useState('DATA');
  const [sortOrder, setSortOrder] = useState('DESC');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [totalItems, setTotalItems] = useState(0);
  const [stats, setStats] = useState({ total: 0, pacientesUnicos: 0, masculino: 0, feminino: 0, hoje: 0 });
  const [filters, setFilters] = useState({ usuarios: [], situacoes: [], locais: [] });
  const [convenioStats, setConvenioStats] = useState([]);
  const [convenioStatsCollapsed, setConvenioStatsCollapsed] = useState(true);

  const [fetchTrigger, setFetchTrigger] = useState(0);

  // Auto-fetch on pagination/sort changes and manual triggers (also handles initial mount)
  useEffect(() => {
    fetchAtendimentos();
  }, [currentPage, itemsPerPage, sortBy, sortOrder, fetchTrigger]);

  const handleSearch = () => {
    if (currentPage === 1) {
      setFetchTrigger(prev => prev + 1);
    } else {
      setCurrentPage(1);
    }
  };

  const fetchAtendimentos = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: itemsPerPage.toString(),
        sortBy,
        sortOrder
      });

      if (searchInput) params.append('search', searchInput);
      if (dataInicio) params.append('dataInicio', dataInicio);
      if (dataFim) params.append('dataFim', dataFim);
      if (usuario) params.append('usuario', usuario);
      if (situacao) params.append('situacao', situacao);
      if (pacienteId) params.append('pacienteId', pacienteId);
      if (convenio) params.append('convenio', convenio);
      if (local) params.append('local', local);

      const response = await axios.get(`${API_URL}/atendimentos?${params.toString()}`);
      const { data, total, filters: responseFilters, convenioStats } = response.data;

      setAtendimentos(data);
      setTotalItems(total);
      setConvenioStats(convenioStats || []);

      setStats(response.data.totais || { total: 0, pacientesUnicos: 0, masculino: 0, feminino: 0, hoje: 0 });
      setFilters(responseFilters || { usuarios: [], situacoes: [], locais: [] });
    } catch (err) {
      console.error('Erro ao buscar atendimentos:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'ASC' ? 'DESC' : 'ASC');
    } else {
      setSortBy(field);
      setSortOrder('ASC');
    }
  };

  const getSortIcon = (field) => {
    if (sortBy !== field) return '↕️';
    return sortOrder === 'ASC' ? '↑' : '↓';
  };

  // formatDate importado de ../utils/formatDate

  const clearFilters = () => {
    setSearchInput('');
    setUsuario('');
    setSituacao('');
    setPacienteId('');
    setConvenio('');
    setLocal('');
    navigate('/atendimentos'); // Remove URL params
    const hoje = new Date();
    const seteDiasAtras = new Date();
    seteDiasAtras.setDate(hoje.getDate() - 7);
    setDataInicio(seteDiasAtras.toISOString().split('T')[0]);
    setDataFim(hoje.toISOString().split('T')[0]);
    setCurrentPage(1);
    setFetchTrigger(prev => prev + 1);
  };

  if (loading && atendimentos.length === 0) {
    return <div className="loading">Carregando atendimentos...</div>;
  }

  const styles = {
    pageHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      marginBottom: '24px',
      flexWrap: 'wrap',
      gap: '12px',
    },
    pageIcon: {
      fontSize: '1.5rem',
      lineHeight: 1,
    },
    pageTitle: {
      fontSize: '1.5rem',
      fontWeight: '700',
      color: '#1a1a2e',
      margin: '0 0 2px 0',
    },
    pageSubtitle: {
      fontSize: '0.85rem',
      color: '#666',
      margin: 0,
    },
    btnPesquisar: {
      padding: '8px 20px',
      backgroundColor: '#2563eb',
      color: '#fff',
      border: 'none',
      borderRadius: '6px',
      cursor: 'pointer',
      fontSize: '0.85rem',
      fontWeight: '600',
      whiteSpace: 'nowrap',
    },
  };

  return (
    <div className="data-page">
      <div style={styles.pageHeader}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span style={styles.pageIcon}>🏥</span>
          <div>
            <h1 style={styles.pageTitle}>Atendimentos</h1>
            <p style={styles.pageSubtitle}>
              Exibindo {stats.total} de {totalItems} atendimentos
              {(searchInput || dataInicio || dataFim || usuario || situacao || pacienteId || convenio || local) && ' (filtrado)'}
            </p>
          </div>
        </div>
      </div>

      {pacienteId && (
        <div style={{
          background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
          color: 'white',
          padding: '1rem 1.5rem',
          borderRadius: '12px',
          marginBottom: '1.5rem',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)'
        }}>
          <div>
            <strong>👤 Filtrando por Paciente ID: {pacienteId}</strong>
          </div>
          <button
            onClick={clearFilters}
            style={{
              background: 'rgba(255, 255, 255, 0.2)',
              border: 'none',
              color: 'white',
              padding: '0.5rem 1rem',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: '600',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.3)'}
            onMouseLeave={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.2)'}
          >
            ✕ Remover Filtro
          </button>
        </div>
      )}

      {convenio && (
        <div style={{
          background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
          color: 'white',
          padding: '1rem 1.5rem',
          borderRadius: '12px',
          marginBottom: '1.5rem',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          boxShadow: '0 4px 12px rgba(139, 92, 246, 0.3)'
        }}>
          <div>
            <strong>🏥 Filtrando por Convênio: {convenio}</strong>
          </div>
          <button
            onClick={() => setConvenio('')}
            style={{
              background: 'rgba(255, 255, 255, 0.2)',
              border: 'none',
              color: 'white',
              padding: '0.5rem 1rem',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: '600',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.3)'}
            onMouseLeave={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.2)'}
          >
            ✕ Remover Filtro
          </button>
        </div>
      )}

      {/* Distribuição por Convênios */}
      {!loading && convenioStats.length > 0 && (
        <div style={{ marginBottom: '20px', background: 'white', borderRadius: '12px', padding: '1rem', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
          <div
            onClick={() => setConvenioStatsCollapsed(!convenioStatsCollapsed)}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              cursor: 'pointer',
              userSelect: 'none'
            }}
          >
            <h3 style={{ margin: 0, fontSize: '1.1rem', color: '#333' }}>📊 Distribuição por Convênio (respeitando filtros)</h3>
            <span style={{ fontSize: '1.5rem', color: '#6b7280', transition: 'transform 0.3s', transform: convenioStatsCollapsed ? 'rotate(0deg)' : 'rotate(180deg)' }}>
              ▼
            </span>
          </div>

          {!convenioStatsCollapsed && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '15px', marginTop: '15px' }}>
              {convenioStats.map((stat, index) => (
                <div
                  key={index}
                  onClick={() => setConvenio(stat.CONVENIO || 'Particular')}
                  style={{
                    background: `linear-gradient(135deg, ${
                      index % 5 === 0 ? '#667eea 0%, #764ba2 100%' :
                      index % 5 === 1 ? '#3b82f6 0%, #2563eb 100%' :
                      index % 5 === 2 ? '#10b981 0%, #059669 100%' :
                      index % 5 === 3 ? '#f59e0b 0%, #d97706 100%' :
                      '#ef4444 0%, #dc2626 100%'
                    })`,
                    padding: '20px',
                    borderRadius: '10px',
                    color: 'white',
                    textAlign: 'center',
                    minWidth: '180px',
                    cursor: 'pointer',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    border: convenio === (stat.CONVENIO || 'Particular') ? '3px solid white' : 'none'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-4px)';
                    e.currentTarget.style.boxShadow = '0 8px 20px rgba(0,0,0,0.3)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <h3 style={{ margin: '0 0 5px 0', fontSize: '2rem' }}>{stat.TOTAL}</h3>
                  <p style={{ margin: 0, opacity: 0.9 }}>{stat.CONVENIO || 'Sem convênio'}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="filters-section">
        <div className="search-bar" style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <input
            type="text"
            placeholder="Buscar por protocolo, nome do paciente ou CPF... (pressione Enter)"
            value={searchInput}
            onChange={(e) => {
              setSearchInput(e.target.value);
              // Remove filtros de data quando buscar por nome/protocolo/CPF
              if (e.target.value) {
                setDataInicio('');
                setDataFim('');
              }
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleSearch();
              }
            }}
            className="search-input"
          />
          <button onClick={handleSearch} style={styles.btnPesquisar}>
            Pesquisar
          </button>
        </div>

        <div className="filters-row">
          <div className="filter-group">
            <label>Data Início:</label>
            <input
              type="date"
              value={dataInicio}
              onChange={(e) => setDataInicio(e.target.value)}
              className="filter-select"
            />
          </div>

          <div className="filter-group">
            <label>Data Fim:</label>
            <input
              type="date"
              value={dataFim}
              onChange={(e) => setDataFim(e.target.value)}
              className="filter-select"
            />
          </div>

          <div className="filter-group">
            <label>Usuário:</label>
            <select
              value={usuario}
              onChange={(e) => setUsuario(e.target.value)}
              className="filter-select"
            >
              <option value="">Todos</option>
              {filters.usuarios.map((u, idx) => (
                <option key={idx} value={u}>{u}</option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Situação:</label>
            <select
              value={situacao}
              onChange={(e) => setSituacao(e.target.value)}
              className="filter-select"
            >
              <option value="">Todas</option>
              {filters.situacoes.map((s, idx) => (
                <option key={idx} value={s}>{s}</option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Local:</label>
            <select
              value={local}
              onChange={(e) => setLocal(e.target.value)}
              className="filter-select"
            >
              <option value="">Todos</option>
              {filters.locais.map((l, idx) => (
                <option key={idx} value={l}>{l}</option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Por página:</label>
            <select
              value={itemsPerPage}
              onChange={(e) => setItemsPerPage(Number(e.target.value))}
              className="filter-select"
            >
              <option value="10">10</option>
              <option value="20">20</option>
              <option value="50">50</option>
              <option value="100">100</option>
            </select>
          </div>

          {(searchInput || usuario || situacao || local) && (
            <button onClick={clearFilters} className="clear-filters-btn">
              Limpar Filtros
            </button>
          )}
        </div>
      </div>

      {/* Resumo dos Dados Filtrados */}
      <div className="compact-stats">
        <div className="compact-stat-card blue">
          <div className="compact-stat-value">{(stats.total || 0).toLocaleString('pt-BR')}</div>
          <div className="compact-stat-label">Total Atendimentos</div>
        </div>
        <div className="compact-stat-card green">
          <div className="compact-stat-value">{(stats.pacientesUnicos || 0).toLocaleString('pt-BR')}</div>
          <div className="compact-stat-label">Pacientes Únicos</div>
        </div>
        <div className="compact-stat-card cyan">
          <div className="compact-stat-value">{(stats.masculino || 0).toLocaleString('pt-BR')}</div>
          <div className="compact-stat-label">Masculino</div>
        </div>
        <div className="compact-stat-card pink">
          <div className="compact-stat-value">{(stats.feminino || 0).toLocaleString('pt-BR')}</div>
          <div className="compact-stat-label">Feminino</div>
        </div>
        <div className="compact-stat-card orange">
          <div className="compact-stat-value">{(stats.hoje || 0).toLocaleString('pt-BR')}</div>
          <div className="compact-stat-label">Hoje</div>
        </div>
      </div>

      <div className="table-card">
        <div className="table-responsive">
          <table className="data-table compact-table">
            <thead>
              <tr>
                <th onClick={() => handleSort('PROTOCOLO')} style={{ cursor: 'pointer', width: '120px' }}>
                  Protocolo {getSortIcon('PROTOCOLO')}
                </th>
                <th onClick={() => handleSort('PACIENTE_NOME')} style={{ cursor: 'pointer' }}>
                  Paciente {getSortIcon('PACIENTE_NOME')}
                </th>
                <th onClick={() => handleSort('DATA')} style={{ cursor: 'pointer', width: '150px' }}>
                  Data {getSortIcon('DATA')}
                </th>
                <th style={{ width: '80px' }}>Local</th>
                <th style={{ width: '140px' }}>Convênio</th>
              </tr>
            </thead>
            <tbody>
              {atendimentos.map((atend) => (
                <React.Fragment key={atend.ID}>
                  <tr
                    onClick={() => navigate(`/atendimentos/${atend.ID}`)}
                    style={{ cursor: 'pointer' }}
                    className="clickable-row"
                  >
                    <td rowSpan="2" style={{ verticalAlign: 'top', paddingTop: '16px' }}>
                      <strong style={{ fontSize: '1.1rem', color: '#3b82f6' }}>{atend.PROTOCOLO}</strong>
                      <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '2px' }}>ID: {atend.ID}</div>
                    </td>
                    <td>
                      <strong style={{ fontSize: '1.05rem', color: '#1f2937' }}>{atend.PACIENTE_NOME || '-'}</strong>
                      <div style={{ marginTop: '4px', color: '#6b7280', fontSize: '0.9rem' }}>
                        CPF: {atend.PACIENTE_CPF || '-'}
                      </div>
                    </td>
                    <td rowSpan="2" style={{ verticalAlign: 'top', paddingTop: '16px' }}>
                      <div style={{ fontWeight: '600' }}>{formatDate(atend.DATA)}</div>
                      {atend.HORARIO && (
                        <div style={{ fontSize: '0.85rem', color: '#6b7280', marginTop: '2px' }}>
                          {atend.HORARIO}
                        </div>
                      )}
                    </td>
                    <td rowSpan="2" style={{ verticalAlign: 'top', paddingTop: '16px', fontSize: '0.9rem', textAlign: 'center' }}>
                      {atend.LOCAL ? atend.LOCAL.trim() : '-'}
                    </td>
                    <td rowSpan="2" style={{ verticalAlign: 'top', paddingTop: '16px' }}>
                      <span style={{
                        padding: '4px 8px',
                        borderRadius: '6px',
                        fontSize: '0.8rem',
                        fontWeight: '600',
                        background: atend.CONVENIO && atend.CONVENIO !== 'Particular' ? '#e0f2fe' : '#f3f4f6',
                        color: atend.CONVENIO && atend.CONVENIO !== 'Particular' ? '#0c4a6e' : '#6b7280',
                        display: 'inline-block'
                      }}>
                        {atend.CONVENIO || 'Particular'}
                      </span>
                    </td>
                  </tr>
                  <tr onClick={() => navigate(`/atendimentos/${atend.ID}`)} style={{ cursor: 'pointer' }} className="clickable-row">
                    <td style={{ borderTop: 'none', paddingTop: '0', color: '#6b7280', fontSize: '0.9rem' }}>
                      {atend.PACIENTE_SEXO === 'M' ? '👨' : atend.PACIENTE_SEXO === 'F' ? '👩' : '👤'} {atend.PACIENTE_SEXO === 'M' ? 'Masculino' : atend.PACIENTE_SEXO === 'F' ? 'Feminino' : 'Não informado'}
                      {atend.IDADE_PACIENTE && ` • Idade: ${atend.IDADE_PACIENTE} anos`}
                    </td>
                  </tr>
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Paginação */}
      <Pagination
        currentPage={currentPage}
        totalPages={Math.ceil(totalItems / itemsPerPage)}
        onPageChange={setCurrentPage}
        itemsPerPage={itemsPerPage}
        totalItems={totalItems}
      />
    </div>
  );
}

export default Atendimentos;
